/**
 * Posters for HTML cards nobody opened (Part 07 decision 11). In the owner
 * window, the first card in or just below the wall's view whose page has
 * no poster for the app's theme, once it stayed first for a moment, is
 * rendered read-only and offscreen and captured with the frame's own
 * capture (`useHtmlPosterCapture`); then the next one. One page at a time;
 * nothing renders, and no timer runs, while every page has its poster.
 */
import {
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";
import type { FrameThumbnailTheme } from "@/api/frame-listing";
import {
  isFrameEditing,
  useFrameEditController,
} from "@/components/shapes/shared/frame-edit-controller";
import {
  subscribeWallView,
  wallCardIdsNearView,
} from "@/components/wall/wall-view-adapter";
import { useWindowRole } from "@/desktop/use-window-role";
import type { FrameData } from "@/lib/frame-data";
import { ensureFrameDetail } from "@/lib/frame-detail-requests";
import { useFramesStore } from "@/stores/frames-store";
import { EditableHtmlIframe, detectEmbedSrc } from "./editable-html-iframe";
import { getInitialThemeMode } from "./html-frame-theme";
import { hasThemePoster, usePosterTheme } from "./html-poster-theme";
import { useHtmlPosterCapture } from "./use-html-poster-capture";

/** The offscreen page: a laptop window's first screen (16:10). */
const PAGE_WIDTH = 1024;
const PAGE_HEIGHT = 640;
const OFFSCREEN_STYLE = {
  left: -2 * PAGE_WIDTH,
  width: PAGE_WIDTH,
  height: PAGE_HEIGHT,
};

/**
 * A candidate mounts once it stayed first this long, so a scroll passing
 * over cards loads none of them.
 */
const SETTLE_MS = 400;
/** A page that has not ended by then is passed, so it can't stall the rest. */
const WATCHDOG_MS = 15_000;

/**
 * Pages this session moved past (captured, rejected, an embed, empty, not
 * loadable or out of time), by frame, theme and version: a rewritten page
 * is tried again.
 */
const passed = new Set<string>();

function passKey(frame: FrameData, theme: FrameThumbnailTheme): string {
  return `${frame.frameId}:${theme}:${frame.updatedAt ?? ""}`;
}

/** What the store alone tells: an HTML page with no poster to show here. */
function lacksPoster(
  frame: FrameData | undefined,
  theme: FrameThumbnailTheme,
): frame is FrameData {
  return (
    frame?.frameType === "html" &&
    frame.status !== "streaming" &&
    !hasThemePoster(frame.metadata?.thumbnail, theme) &&
    !passed.has(passKey(frame, theme))
  );
}

/**
 * The first card in or just below the view (this project's own frames)
 * needing a poster. The wall's layout is read only while some page in the
 * store needs one.
 */
function nextPage(theme: FrameThumbnailTheme): string | null {
  const { framesById } = useFramesStore.getState();
  const anyLacks = Object.values(framesById).some((frame) =>
    lacksPoster(frame, theme),
  );
  if (!anyLacks) return null;
  return (
    wallCardIdsNearView().find(
      (id) => lacksPoster(framesById[id], theme) && !isFrameEditing(id),
    ) ?? null
  );
}

/** Wall and store changes, at most once per animation frame. */
function subscribe(listener: () => void): () => void {
  let frame = 0;
  const scheduled = () => {
    if (frame) return;
    frame = requestAnimationFrame(() => {
      frame = 0;
      listener();
    });
  };
  const stopWall = subscribeWallView(scheduled);
  const stopFrames = useFramesStore.subscribe(scheduled);
  return () => {
    stopWall();
    stopFrames();
    cancelAnimationFrame(frame);
  };
}

/** The candidate `nextId`, once it has stayed first for `SETTLE_MS`. */
function useSettledPage(nextId: string | null): string | null {
  const [run, setRun] = useState({ id: nextId, settled: false });
  if (run.id !== nextId) setRun({ id: nextId, settled: false });
  useEffect(() => {
    if (!run.id || run.settled) return;
    const timer = window.setTimeout(
      () =>
        setRun((current) =>
          current === run ? { ...run, settled: true } : current,
        ),
      SETTLE_MS,
    );
    return () => window.clearTimeout(timer);
  }, [run]);
  return run.id === nextId && run.settled ? nextId : null;
}

/** One page, loaded if the wall holds only its card, rendered offscreen. */
function BackfillPage({
  frame,
  onPass,
}: {
  frame: FrameData;
  onPass: (frameId: string) => void;
}) {
  const { frameId } = frame;
  const loaded = frame.contentState !== "card";
  const html =
    loaded && typeof frame.content.html === "string" ? frame.content.html : "";
  const embedSrc = useMemo(() => (html ? detectEmbedSrc(html) : null), [html]);
  const { editing } = useFrameEditController(frameId);
  const pageRef = useRef<HTMLDivElement>(null);
  const getIframe = useCallback(
    () => pageRef.current?.querySelector("iframe"),
    [],
  );
  const pass = useCallback(() => onPass(frameId), [frameId, onPass]);

  useEffect(() => {
    const timer = window.setTimeout(pass, WATCHDOG_MS);
    return () => window.clearTimeout(timer);
  }, [pass]);

  useEffect(() => {
    if (loaded) return;
    const controller = new AbortController();
    ensureFrameDetail(frameId, { signal: controller.signal }).catch(() => {
      if (!controller.signal.aborted) pass();
    });
    return () => controller.abort();
  }, [frameId, loaded, pass]);

  // An empty page or another site's embed has no poster to take.
  const nothingToCapture = loaded && (!html || embedSrc !== null);
  useEffect(() => {
    if (nothingToCapture) pass();
  }, [nothingToCapture, pass]);

  useHtmlPosterCapture({
    frameId,
    data: frame,
    html,
    crossOriginEmbed: embedSrc !== null,
    editing,
    getIframe,
    onEnded: pass,
  });

  if (!html || embedSrc) return null;
  return (
    <div
      ref={pageRef}
      className="pointer-events-none fixed top-0 overflow-hidden"
      style={OFFSCREEN_STYLE}
      aria-hidden="true"
      inert
      data-testid="html-poster-backfill"
    >
      <EditableHtmlIframe
        frameId={frameId}
        html={html}
        title={frame.title}
        className="h-full w-full border-0"
        themeMode={getInitialThemeMode(frame.metadata)}
        showScrollbars={false}
      />
    </div>
  );
}

/** Mounted once by the shown wall (`wall-view.tsx`). */
export function HtmlPosterBackfill() {
  const owner = useWindowRole() === "owner";
  const theme = usePosterTheme();
  const [, movedOn] = useReducer((count: number) => count + 1, 0);
  const nextId = useSyncExternalStore(subscribe, () =>
    owner ? nextPage(theme) : null,
  );
  const shownId = useSettledPage(nextId);
  const frame = useFramesStore((state) =>
    shownId ? state.framesById[shownId] : undefined,
  );
  const pass = useCallback(
    (frameId: string) => {
      const current = useFramesStore.getState().framesById[frameId];
      if (current) passed.add(passKey(current, theme));
      movedOn();
    },
    [theme],
  );
  if (!frame) return null;
  return <BackfillPage key={frame.frameId} frame={frame} onPass={pass} />;
}
